import { useMutation, useQuery, useQueryClient, type UseQueryResult } from '@tanstack/react-query'
import { api } from '../../api/client'
import type { components } from '../../api/generated/schema'
import { toComicSheet, type ComicSheet, type ComicSummary } from './types'

type Schema = components['schemas']

export const comicKeys = {
  all: ['comics'] as const,
  lists: () => [...comicKeys.all, 'list'] as const,
  list: (campaignId: number, page: number, pageSize: number) =>
    [...comicKeys.lists(), campaignId, page, pageSize] as const,
  sheets: () => [...comicKeys.all, 'sheet'] as const,
  sheet: (comicId: number) => [...comicKeys.sheets(), comicId] as const,
}

export interface ComicListPage {
  items: ComicSummary[]
  page: number
  pageSize: number
  totalCount: number
  totalPages: number
}

/** A campaign's comics, newest first. Any member may read them. */
export function useComicList(
  campaignId: number,
  page = 1,
  pageSize = 50,
): UseQueryResult<ComicListPage> {
  return useQuery({
    queryKey: comicKeys.list(campaignId, page, pageSize),
    enabled: Number.isFinite(campaignId),
    queryFn: async () => {
      const result = await api.get<Schema['ComicSummaryDtoPagedResult']>(
        `/campaigns/${campaignId}/comics`,
        { query: { page, pageSize } },
      )

      return {
        items: result.items ?? [],
        page: result.page ?? page,
        pageSize: result.pageSize ?? pageSize,
        totalCount: result.totalCount ?? 0,
        totalPages: result.totalPages ?? 0,
      }
    },
  })
}

/** One comic: core, and its scenes with their pages and images. */
export function useComicSheet(comicId: number): UseQueryResult<ComicSheet> {
  return useQuery({
    queryKey: comicKeys.sheet(comicId),
    enabled: Number.isFinite(comicId),
    queryFn: async () => toComicSheet(await api.get<Schema['ComicSheetDto']>(`/comics/${comicId}`)),
  })
}

/**
 * Creates a comic against one of the campaign's cases. Game Master only.
 *
 * Answers `201` with a whole sheet rather than an envelope patch — there is nothing to
 * patch yet — so the new sheet seeds its own cache entry and the list, which this write
 * moved, is invalidated. Those are the two create-shaped exceptions to the never-refetch
 * rule, and they are the same two every other domain has.
 */
export function useCreateComic(campaignId: number) {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async (input: Schema['ComicRequest']) =>
      toComicSheet(
        await api.post<Schema['ComicSheetDto']>(`/campaigns/${campaignId}/comics`, input),
      ),

    onSuccess: (sheet) => {
      void queryClient.invalidateQueries({ queryKey: comicKeys.lists() })
      queryClient.setQueryData(comicKeys.sheet(sheet.comicId), sheet)
    },
  })
}

/**
 * Deletes a comic and everything under it. Answers 204, so there is no sheet left to
 * patch — the entry is dropped rather than emptied.
 */
export function useDeleteComic() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: (comicId: number) => api.delete<void>(`/comics/${comicId}`),

    onSuccess: (_result, comicId) => {
      queryClient.removeQueries({ queryKey: comicKeys.sheet(comicId) })
      void queryClient.invalidateQueries({ queryKey: comicKeys.lists() })
    },
  })
}
